<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Utilisateurs </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <TableHeaderButtons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reloadData="reloadData"
            @exportTable="exportTable"
            @updateSearchPanel="updateSearchPanel"
            :hasSelectedLines="selectedUserIds.length > 0"
          />
        </div>
      </v-toolbar>
      <Table
        :loading="loading"
        :searchField="true"
        :columns="columns"
        :items="users"
        :showSelect="true"
        storageKey="userTablePaginationState"
        @edit="edit"
        @rowClicked="rowClicked"
        @selectionChanged="selectionChanged"
      />
    </v-card>

    <v-overlay :model-value="loading">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <SearchPanel
      :rightDrawer="rightDrawer"
      @cancelSearch="cancelSearch"
      @searchData="searchUsers"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="First Name" v-model="searchFilter.contain.name" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Last Name" v-model="searchFilter.contain.lastname" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Téléphone" v-model="searchFilter.contain.telephone" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Email" v-model="searchFilter.contain.email" />
        </v-col>
      </v-row>
    </SearchPanel>

    <ConfirmDialog
      v-model="dialog"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
      confirm-button-text="Confirmer"
      cancel-button-text="Annuler"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import { buildSearchFilters, buildJsonServerQuery } from "@/utils/app-util";

import { useAppStore } from "@/store/app";
import { createAndSaveDocument } from "@/utils/docx-util";
import { getUserExport, TableColumn, User } from "@/types";
import { deleteUser, getAllUsers, getUserById, searchForUsers } from "@/utils/user-api";
import { Entity } from "@/types";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les utilisateurs sélectionnés?");

const showSearchPanel = ref(false);
const selectedUserIds = ref<string[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

const searchFilter = ref({
  contain: {
    name: "",
    lastname: "",
    email: "",
    telephone: "",
  },
});

const query = ref("");

const columns: TableColumn[] = [
  { title: "Prénom", key: "firstname" },
  { title: "Nom de famille", key: "lastname" },
  { title: "Courriel", key: "email" },
  { title: "Téléphone", key: "telephone" },
  { title: "Nom d'utilisateur", key: "username" },
  { title: "Roles", key: "roles" },
  { title: "", key: "actions", sortable: false },
];

const toolTipText = "un utilisateur";

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (val: boolean) => (showSearchPanel.value = val),
});

function selectionChanged(selected: string[]) {
  selectedUserIds.value = [...selected];
}

function rowClicked(item: Entity) {
  router.push(`users/${item.id}`);
}

function edit(item: Entity) {
  router.push(`users/${item.id}`);
}

function add() {
  router.push({ name: "newuser" });
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  try {
    loading.value = true;
    dialog.value = false;
    await deleteUser(selectedUserIds.value);
    await fetchUsers();
    selectedUserIds.value = [];
    appStore.sendSuccessNotice("L'utilisateur a été supprimé.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

function searchUsers() {
  buildSearchFilters(searchFilter.value);
  query.value = buildJsonServerQuery(searchFilter.value);
  _searchUsers(query.value);
  showSearchPanel.value = false;
}

async function _searchUsers(searchQuery: string) {
  try {
    loading.value = true;
    users.value = await searchForUsers(searchQuery);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function reloadData() {
  query.value = "";
  fetchUsers();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const userIds = [...selectedUserIds.value];
  if (userIds.length === 0) {
    return;
  }

  try {
    loading.value = true;
    const selectedUsers = await Promise.all(userIds.map((id) => getUserById(id)));

    createAndSaveDocument(
      userExportFileName(),
      userExportTemplatePath(),
      getUserExport(selectedUsers)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function userExportFileName() {
  return "export_utilisateurs";
}

function userExportTemplatePath() {
  return "../templates/ExportUtilisateursTemplate.docx";
}

function updateSearchPanel() {
  rightDrawer.value = !rightDrawer.value;
}

function cancelSearch() {
  showSearchPanel.value = false;
}

async function fetchUsers() {
  try {
    loading.value = true;
    users.value = await getAllUsers();
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchUsers();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
